<template lang="pug">
    form.fields(action="javascript://")
        .fields__element
            label.fields__label.fields__label--small {{ $i18n.t('fields.fields_status') }}
            cs-multiselect.fields__input(
                :multiple="false",
                :options="statuses",
                :show-labels="false",
                :allow-empty="true",
                :custom-label="getStatusName",
                :placeholder="$i18n.t('fields.fields_anyStatus')",
                v-model="form.status",
                :searchable="false"
            )
        .fields__element
        .fields__element
            label.fields__label.fields__label--small {{ $i18n.t('fields.fields_period') }}
            cs-datepicker.fields__input.datepicker-input-container--no-margin(
                :language="user.locale",
                :disabled-dates="form.dateTo ? {from: new Date(form.dateTo)} : {}",
                :format="customFormatter",
                v-model="form.dateFrom"
            )
            .fields__divider —
            cs-datepicker.fields__input.datepicker-input-container--no-margin(
                :language="user.locale",
                :disabled-dates="form.dateFrom ? {to: new Date(form.dateFrom)} : {}",
                :format="customFormatter",
                v-model="form.dateTo"
            )
        .fields__element
            .fields__period-buttons-container
                a.fields__period-button(
                    v-for="periodValue in periods"
                    @click.prevent="setPeriod(periodValue)"
                )
                    | {{ $i18n.t(`fields.fields_${periodValue}Period`) }}
        .fields__element.fields__element--fluid
            cs-button(
                color="blue",
                pattern="primary-outline",
                html-tag="a"
                @click.prevent="resetResults",
                narrow
            )
                | {{ $i18n.t('fields.fields_dropParameters') }}
</template>

<script>
    import _ from 'lodash';
    import searchParamsStore from '../../utils/searchParamsStore';
    import statusFieldMixin from '../../utils/statusFieldMixin';
    import { mapState } from 'vuex';
    import moment from 'moment';

    export default {
        mixins: [searchParamsStore, statusFieldMixin],
        data() {
            return {
                form: {
                    status: null,
                    dateTo: null,
                    dateFrom: null,
                },
                periods: ['day', 'week', 'month'],
                statuses: ['active', 'notActive', 'archived'],
            };
        },
        mounted() {
            this.sendForm();
        },
        watch: {
            '$route.query'(val) {
                if (_.isEmpty(val)) {
                    this.form = _.cloneDeep(this.localSearchParams) || {
                        dateFrom: this.$moment()
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                        dateTo: this.$moment()
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                        status: null,
                    };
                }
            },
            'form.status'() {
                this.updateList();
            },
            'form.dateFrom'() {
                this.updateList();
            },
            'form.dateTo'() {
                this.updateList();
            },
        },
        methods: {
            sendForm() {
                if (this.form.dateFrom) {
                    this.form.dateFrom = moment(this.form.dateFrom).format('YYYY-MM-DD');
                }
                if (this.form.dateTo) {
                    this.form.dateTo = moment(this.form.dateTo).format('YYYY-MM-DD');
                }
                this.$emit('search', this.form);
            },
            setPeriod(range) {
                this.form.dateFrom = moment()
                    .startOf(range)
                    .format();
                this.form.dateTo = moment()
                    .endOf(range)
                    .format();
            },
            resetResults() {
                this.form.status = null;
                this.form.dateFrom = moment()
                    .startOf('month')
                    .format('YYYY-MM-DD');
                this.form.dateTo = moment()
                    .endOf('month')
                    .format('YYYY-MM-DD');
                this.$emit('search', this.form);
            },
            customFormatter(date) {
                return moment(date).format('D MMM YYYY');
            },
            updateList: _.debounce(function() {
                this.sendForm();
            }, 1000),
        },
        computed: {
            ...mapState({
                user: state => state.auth.user,
            }),
        },
    };
</script>

<style scoped lang="scss">
    .fields {
        display: flex;
        flex-wrap: wrap;
        padding: 0 15px;
    }

    .fields__element {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        box-sizing: border-box;
        width: 100%;
        @include media($md) {
            flex-direction: row;
            align-items: center;
        }
        @include media($xlg) {
            width: 50%;
        }
    }

    .fields__element:nth-child(odd) {
        @include media($xlg) {
            padding-right: 40px;
        }
    }

    .fields__element:last-child {
        margin-bottom: 0;
    }

    .fields__element--fluid {
        width: auto;
    }

    .fields__input {
        min-width: 0;
        flex: 1 1 auto;
        margin-bottom: 0;
    }

    .fields__element--period {
        @include media($xlg) {
            display: flex;
            align-items: center;
            padding: 0;
            width: 100%;
        }
    }

    .fields__divider {
        text-align: center;
        @include media($md) {
            margin: 0 $space-x-small;
        }
    }

    .fields__label {
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        min-width: 6.5em;
        @include media($md) {
            text-align: right;
        }
        @include media($xlg) {
            margin-bottom: 0;
        }
    }

    .fields__label--small {
        @include media($xlg) {
            min-width: 5em;
        }
    }

    .fields__period-buttons-container {
        display: flex;
        flex-wrap: wrap;
        @include media($xlg) {
            margin: 0 20px 0 0;
            flex-wrap: nowrap;
            &:last-child {
                margin-right: 0;
            }
        }
    }

    .fields__period-buttons-container:before {
        content: '';
        display: none;
        margin-right: 20px;
        min-width: 6.5em;
        @include media($md) {
            display: block;
        }
        @include media($xlg) {
            display: none;
        }
    }

    .fields__period-button {
        white-space: nowrap;
        text-align: center;
        flex: 1 1 auto;
        color: $hsl-color-blue-56;
        text-decoration: underline;
        &:hover {
            color: $hsl-color-blue-56;
            text-decoration: none;
        }
        margin: 0 20px;
        @include media($xlg) {
            &:last-child {
                margin-right: 0;
            }
        }
    }

    .fields__element--search-neighbour {
        position: relative;
    }

    .fields__search {
        position: absolute;
        right: 0;
    }
</style>
